/**
 * Luca's Quest Service
 * 
 * Tracks conversation topics and time to trigger random quiz missions.
 * 
 * Features:
 * - Monitors conversation for 5+ minutes on a topic
 * - Generates contextual quizzes based on discussed topics
 * - Rewards tokens based on performance
 * - Quest cooldown of 10 minutes between quests
 */

import { Message, Role } from '../types';

export interface QuestConfig {
    minConversationTime: number;       // 5 minutes in ms
    questCooldown: number;             // 10 minutes in ms
    minItems: number;                  // 10 items minimum
    maxItems: number;                  // 50 items maximum
    baseTokenReward: number;           // Base reward per correct answer
    enableMixedQuestions: boolean;     // Enable mixed question types
    questionTypes: {
        multipleChoice: boolean;
        trueFalse: boolean;
        fillInBlank: boolean;
        shortAnswer: boolean;
        computation: boolean;
        essay: boolean;
    };
    questionTypeWeights: {
        multipleChoice: number;        // Weight for distribution
        trueFalse: number;
        fillInBlank: number;
        shortAnswer: number;
        computation: number;
        essay: number;
    };
}

export type QuestionType = 'multipleChoice' | 'trueFalse' | 'fillInBlank' | 'shortAnswer' | 'computation' | 'essay';

export interface TopicAnalysis {
    mainTopic: string;
    subTopics: string[];
    difficulty: 'easy' | 'medium' | 'hard' | 'expert';
    estimatedComplexity: number;       // 1-10
    keyTerms: string[];
}

export interface QuestionTypeDistribution {
    multipleChoice: number;
    trueFalse: number;
    fillInBlank: number;
    shortAnswer: number;
    computation: number;
    essay: number;
}

export interface QuestData {
    id: string;
    sessionId: string;
    topics: TopicAnalysis;
    itemCount: number;
    difficulty: 'easy' | 'medium' | 'hard' | 'expert';
    timeLimit: number;                 // in minutes
    createdAt: number;
    expiresAt: number;
    status: 'pending' | 'active' | 'completed' | 'cancelled' | 'expired';
    score?: number;
    totalItems?: number;
    tokensRewarded?: number;
    questionDistribution?: QuestionTypeDistribution;
    enabledQuestionTypes?: QuestionType[];
}

export interface QuestResult {
    questId: string;
    score: number;
    totalItems: number;
    difficulty: string;
    tokensRewarded: number;
    bonusTokens: number;
    perfectBonus: boolean;
    streakBonus: number;
    newStreak?: number;
}

// Default configuration - Optimized for frequent engagement
const DEFAULT_CONFIG: QuestConfig = {
    minConversationTime: 1 * 60 * 1000,    // 1 minute (very quick triggering)
    questCooldown: 3 * 60 * 1000,          // 3 minutes (short cooldown)
    minItems: 5,                            // 5 items minimum (easier to complete)
    maxItems: 25,                           // 25 items maximum
    baseTokenReward: 10,                    // Increased base reward
    enableMixedQuestions: true,
    questionTypes: {
        multipleChoice: true,
        trueFalse: true,
        fillInBlank: true,
        shortAnswer: true,
        computation: true,
        essay: false                        // Disabled by default (takes too long)
    },
    questionTypeWeights: {
        multipleChoice: 40,    // 40% of questions (most common)
        trueFalse: 20,         // 20%
        fillInBlank: 15,       // 15%
        shortAnswer: 10,       // 10%
        computation: 15,       // 15%
        essay: 0               // 0% (disabled)
    }
};

// Token reward multipliers
const DIFFICULTY_MULTIPLIERS = {
    easy: 1.0,
    medium: 1.5,
    hard: 2.0,
    expert: 3.0
};

// Bonus percentages
const PERFECT_SCORE_BONUS = 0.5;  // +50% for 100%
const HIGH_SCORE_BONUS = 0.25;    // +25% for 90%+
const STREAK_BONUS_PER_QUEST = 0.1; // +10% per consecutive quest completed

class LucasQuestService {
    private config: QuestConfig;
    private activeQuests: Map<string, QuestData> = new Map();
    private completedQuests: QuestData[] = [];
    private lastQuestTime: Map<string, number> = new Map();
    private conversationStartTimes: Map<string, number> = new Map();
    private questStreak: number = 0;
    private listeners: Set<(quest: QuestData | null) => void> = new Set();

    constructor(config: Partial<QuestConfig> = {}) {
        this.config = { ...this.loadConfig(), ...config };
        this.loadState();
    }

    /**
     * Load saved state from localStorage
     */
    private loadState(): void {
        try {
            const saved = localStorage.getItem('lucas_quest_state');
            if (saved) {
                const state = JSON.parse(saved);
                this.questStreak = state.questStreak || 0;
                this.completedQuests = state.completedQuests || [];

                // Restore last quest times
                if (state.lastQuestTimes) {
                    Object.entries(state.lastQuestTimes).forEach(([sessionId, time]) => {
                        this.lastQuestTime.set(sessionId, time as number);
                    });
                }
            }
        } catch (error) {
            console.error('[LucasQuest] Failed to load state:', error);
        }
    }

    /**
     * Save state to localStorage
     */
    private saveState(): void {
        try {
            const state = {
                questStreak: this.questStreak,
                completedQuests: this.completedQuests.slice(-50), // Keep last 50
                lastQuestTimes: Object.fromEntries(this.lastQuestTime)
            };
            localStorage.setItem('lucas_quest_state', JSON.stringify(state));
        } catch (error) {
            console.error('[LucasQuest] Failed to save state:', error);
        }
    }

    /**
     * Subscribe to quest notifications
     */
    subscribe(callback: (quest: QuestData | null) => void): () => void {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    /**
     * Notify all listeners
     */
    private notify(quest: QuestData | null): void {
        this.listeners.forEach(cb => cb(quest));
    }

    /**
     * Start tracking a conversation
     */
    startTracking(sessionId: string): void {
        if (!this.conversationStartTimes.has(sessionId)) {
            this.conversationStartTimes.set(sessionId, Date.now());
            console.log(`[LucasQuest] Started tracking session: ${sessionId}`);
        }
    }

    /**
     * Check if quest can be triggered for a session
     */
    canTriggerQuest(sessionId: string): boolean {
        // Check cooldown
        const lastQuest = this.lastQuestTime.get(sessionId);
        if (lastQuest && Date.now() - lastQuest < this.config.questCooldown) {
            const remaining = Math.ceil((this.config.questCooldown - (Date.now() - lastQuest)) / 1000 / 60);
            console.log(`[LucasQuest] Cooldown active for ${sessionId}: ${remaining} minutes remaining`);
            return false;
        }

        // Check if already has active quest
        if (this.activeQuests.has(sessionId)) {
            console.log(`[LucasQuest] Session ${sessionId} already has active quest`);
            return false;
        }

        // Check minimum conversation time
        const startTime = this.conversationStartTimes.get(sessionId);
        if (!startTime) {
            console.log(`[LucasQuest] Session ${sessionId} not being tracked yet`);
            return false;
        }

        const conversationTime = Date.now() - startTime;
        if (conversationTime < this.config.minConversationTime) {
            const needed = Math.ceil((this.config.minConversationTime - conversationTime) / 1000);
            console.log(`[LucasQuest] Session ${sessionId} needs ${needed}s more conversation time`);
            return false;
        }

        console.log(`[LucasQuest] Session ${sessionId} is ready for quest trigger!`);
        return true;
    }

    /**
     * Get time until next quest is available
     */
    getTimeUntilNextQuest(sessionId: string): number {
        const lastQuest = this.lastQuestTime.get(sessionId);
        if (!lastQuest) return 0;

        const timeRemaining = this.config.questCooldown - (Date.now() - lastQuest);
        return Math.max(0, timeRemaining);
    }

    /**
     * Analyze conversation topics
     */
    /**
     * Word frequency analysis for detecting key concepts
     */
    analyzeWordFrequency(content: string): Map<string, number> {
        // Remove common words (stop words)
        const stopWords = new Set([
            'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from',
            'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did',
            'will', 'would', 'should', 'could', 'may', 'might', 'can', 'shall', 'must',
            'this', 'that', 'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'them',
            'what', 'when', 'where', 'why', 'how', 'which', 'who', 'whom', 'whose',
            'as', 'if', 'than', 'then', 'so', 'just', 'now', 'very', 'also', 'not', 'no', 'yes',
            'like', 'such', 'about', 'into', 'through', 'during', 'before', 'after', 'above', 'below',
            'between', 'under', 'over', 'again', 'here', 'there', 'all', 'both', 'each', 'few', 'more', 'most',
            'some', 'any', 'only', 'own', 'same', 'other', 'another', 'such', 'no', 'nor', 'not', 'only',
            'question', 'answer', 'please', 'thank', 'thanks', 'help', 'need', 'want', 'know', 'understand'
        ]);

        // Extract words (2+ letters) and count frequency
        const words = content.toLowerCase()
            .replace(/[^\w\s]/g, ' ')
            .split(/\s+/)
            .filter(word => word.length >= 2 && !stopWords.has(word));

        const frequency = new Map<string, number>();
        words.forEach(word => {
            frequency.set(word, (frequency.get(word) || 0) + 1);
        });

        return frequency;
    }

    analyzeTopics(messages: Message[]): TopicAnalysis {
        // Extract content from both user questions and AI responses
        const aiMessages = messages.filter(m => m.role === Role.MODEL);
        const userMessages = messages.filter(m => m.role === Role.USER);

        // FIX: Weight user messages 3x higher for topic detection
        // User questions should determine the topic, not AI explanations
        const userContent = userMessages.map(m => m.content.toLowerCase()).join(' ');
        const aiContent = aiMessages.map(m => m.content.toLowerCase()).join(' ');
        // Repeat user content to increase its weight in keyword matching
        const allContent = `${userContent} ${userContent} ${userContent} ${aiContent}`;

        console.log(`[LucasQuest] Analyzing ${messages.length} messages (${userMessages.length} user, ${aiMessages.length} AI) - User content weighted 3x`);

        // NEW: Analyze word frequency to detect key concepts
        const wordFreq = this.analyzeWordFrequency(allContent);
        const topWords = Array.from(wordFreq.entries())
            .filter(([word, count]) => count >= 3) // Must appear at least 3 times
            .sort((a, b) => b[1] - a[1])
            .slice(0, 15) // Top 15 most frequent terms
            .map(([word, count]) => `${word}(${count}×)`);

        if (topWords.length > 0) {
            console.log(`[LucasQuest] 🔑 Key Repeated Terms:`, topWords.join(', '));
        }

        // Enhanced topic patterns with comprehensive keywords - ULTRA REFINED for maximum accuracy
        const topicPatterns = {
            'Financial Accounting': /\b(journal|journalize|entry|entries|debit|credit|trial balance|adjusted trial|unadjusted|financial statement|balance sheet|statement of financial position|income statement|profit or loss|statement of comprehensive income|pfrs|pas|ifrs|ias|far|afar|revenue|accrual|cash basis|accrual basis|matching principle|revenue recognition|expense recognition|depreciation|amortization|inventory|perpetual inventory|periodic inventory|fifo|lifo|weighted average|specific identification|lower of cost or net realizable|receivable|payable|allowance|doubtful account|bad debt|uncollectible|aging method|percentage of sales|asset|current asset|non.?current asset|liability|current liability|non.?current liability|equity|capital|stockholder.?s equity|retained earnings|accumulated other comprehensive|dividend|cash dividend|stock dividend|liquidating dividend|cash flow|statement of cash flows|direct method|indirect method|operating activit|investing activit|financing activit|free cash flow|straight.?line|double declining|sum.?of.?the.?years|units of production|activity method|book value|net book value|carrying amount|carrying value|fair value|fair value measurement|impairment|impairment loss|recoverable amount|value in use|revaluation|revaluation surplus|cost model|revaluation model|present value|future value|time value|discounting|compounding|effective interest|effective rate|nominal rate|stated rate|face value|maturity value|premium|discount|amortization of premium|amortization of discount|bond|bond payable|note|note payable|note receivable|lease|finance lease|operating lease|lessor|lessee|right.?of.?use asset|lease liability|contingent|contingent liability|contingent asset|provision|warranty|warranty expense|sale|sales return|sales allowance|sales discount|trade discount|purchase return|purchase allowance|purchase discount|consignment|consignor|consignee|installment|installment sale|cost recovery|unrealized profit)\b/gi,

            'Advanced Accounting': /\b(consolidat|consolidated|consolidating|business combination|acquisition method|goodwill|bargain purchase|merger|acquisition|acquiree|acquirer|intercompany|intragroup|upstream|downstream|foreign currency|translation|remeasurement|functional currency|presentation currency|hyperinflation|joint arrangement|joint venture|joint operation|venturi|partnership|admission|withdrawal|retirement|branch|home office|agency|afar|nci|non.?controlling interest|minority interest|parent|subsidiary|associate|significant influence|control|joint control|investment in associate|equity method|cost method|step acquisition|piecemeal|gain on bargain|negative goodwill)\b/gi,

            'Taxation': /\b(tax|taxation|taxes|vat|value.?added|bir|bureau of internal revenue|income tax|taxable income|gross income|ordinary income|capital gain|withholding|expanded withholding|final withholding|creditable withholding|estate tax|donor's tax|donor tax|transfer tax|documentary stamp|excise|percentage tax|capital gains tax|final tax|creditable|ordinary deduction|itemized deduction|optional standard deduction|osd|personal exemption|dependent|nirc|national internal revenue code|revenue regulation|rr|revenue memorandum|rmo|revenue memorandum order|tax code|taxable|exempt|exclusion|deduction|exemption|eopt|8% gross|train law|create act|corporate income tax|cit|individual income tax|compensation income|business income|passive income|fringe benefit|de minimis|allowable deduction|non.?deductible|gross estate|net estate|conjugal|community|exclusive|vanishing deduction|medical expenses|tax credit|foreign tax credit|minimum corporate income tax|mcit|improperly accumulated earnings tax|iaet)\b/gi,

            'Auditing & Assurance': /\b(audit|auditing|auditor|external audit|internal audit|internal control|coso|control environment|risk assessment|control activities|information system|monitoring|substantive procedure|substantive test|test of control|test of details|analytical procedure|assertion|existence|completeness|accuracy|valuation|rights and obligations|presentation|disclosure|audit evidence|sufficient appropriate|audit documentation|working papers|sampling|statistical|non.?statistical|materiality|performance materiality|tolerable misstatement|audit risk|inherent risk|control risk|detection risk|significant risk|engagement|planning|execution|reporting|opinion|unmodified|modified|qualified|adverse|disclaimer|emphasis of matter|other matter|psa|philippine standards on auditing|isa|international standards on auditing|engagement letter|representation letter|management representation|going concern|subsequent event|related party|fraud|error|illegal act|professional skepticism|independence|objectivity|due care|confidentiality|professional competence|quality control|peer review|compliance audit|operational audit|financial audit|special purpose|agreed.?upon procedure|compilation|review|assurance engagement|attestation)\b/gi,

            'Management Accounting / MAS': /\b(cost accounting|management accounting|cost|budget|budgeting|master budget|operating budget|financial budget|static budget|flexible budget|variance|variance analysis|favorable|unfavorable|price variance|quantity variance|spending variance|efficiency variance|volume variance|cvp|cost.?volume.?profit|break.?even|contribution margin|contribution margin ratio|target profit|margin of safety|operating leverage|standard cost|standard costing|normal costing|actual costing|mas|management advisory|activity.?based costing|abc|activity.?based management|abm|transfer pricing|decision making|make or buy|accept or reject|sell or process|special order|relevant cost|differential cost|opportunity cost|sunk cost|avoidable|unavoidable|incremental|variable cost|fixed cost|mixed cost|semi.?variable|step cost|direct cost|indirect cost|overhead|manufacturing overhead|applied overhead|predetermined rate|under.?applied|over.?applied|job order|process costing|equivalent units|weighted average method|fifo method|normal spoilage|abnormal spoilage|scrap|rework|defect|joint product|by.?product|split.?off|separable cost|net realizable value|physical units|sales value|constant gross margin|responsibility accounting|cost center|revenue center|profit center|investment center|return on investment|roi|residual income|ri|economic value added|eva|balanced scorecard|key performance indicator|kpi)\b/gi,

            'Business Law / RFBT': /\b(corporation|corporate|articles of incorporation|by.?laws|stockholder|shareholder|board of directors|officer|pre.?emptive right|appraisal right|derivative suit|pierce corporate veil|ultra vires|securities|stock|share|common stock|preferred stock|par value|no par|treasury stock|subscription|dividend|stock dividend|cash dividend|property dividend|liquidating dividend|stock split|partnership|general partner|limited partner|articles of partnership|capital|industrial partner|capitalist|partnership property|admission|retirement|dissolution|winding up|liquidation|agency|principal|agent|third party|actual authority|apparent authority|ratification|trust|trustee|beneficiary|settlor|trust fund|express trust|implied trust|resulting trust|constructive trust|negotiable instrument|promissory note|bill of exchange|check|holder in due course|negotiation|indorsement|dishonor|notice of dishonor|protest|presentment|civil code|contract|obligation|law on obligations|law on contracts|essential requisite|consent|object|cause|void|voidable|unenforceable|rescissible|natural obligation|prestation|debtor|creditor|solidary|joint|real contract|personal|accessory|principal|conditional|pure|term|resolutory|suspensive|fortuitous event|force majeure|fraud|mistake|undue influence|intimidation|simulation|novation|compensation|confusion|remission|loss|impossibility|prescription|legal|conventional|rfbt|regulation of business|revised corporation code|rcc|securities regulation code|src|negotiable instruments law|nil|general banking law|insurance code|intellectual property code|labor code|consumer act|data privacy act|anti.?money laundering|amla|bsp|bangko sentral|sec|securities and exchange)\b/gi,

            'Economics & Finance': /\b(economics|macroeconomics|microeconomics|supply|demand|equilibrium|elasticity|market structure|perfect competition|monopoly|oligopoly|monopolistic competition|gdp|gnp|inflation|deflation|unemployment|fiscal policy|monetary policy|interest rate|exchange rate|balance of payments|comparative advantage|opportunity cost|marginal|utility|finance|financial management|capital budgeting|npv|net present value|irr|internal rate of return|payback period|profitability index|wacc|weighted average cost of capital|capital structure|leverage|financial leverage|operating leverage|working capital|current ratio|quick ratio|debt ratio|equity ratio|return on equity|roe|return on assets|roa|earnings per share|eps|price.?earnings ratio|dividend yield|payout ratio|retained earnings|trade.?off theory|pecking order|modigliani.?miller|capm|capital asset pricing|beta|portfolio|diversification|risk|return|time value of money)\b/gi
        };

        const topicCounts: Record<string, number> = {};
        let maxCount = 0;
        let mainTopic = 'General Accounting';

        // Count matches for each topic
        Object.entries(topicPatterns).forEach(([topic, pattern]) => {
            const matches = allContent.match(pattern) || [];
            topicCounts[topic] = matches.length;
            if (matches.length > maxCount) {
                maxCount = matches.length;
                mainTopic = topic;
            }
        });

        // FIX: Require minimum confidence before declaring a specific topic
        const MIN_CONFIDENCE_MATCHES = 5;
        if (maxCount < MIN_CONFIDENCE_MATCHES) {
            console.log(`[LucasQuest] ⚠️ Low confidence: only ${maxCount} keyword matches (need ${MIN_CONFIDENCE_MATCHES}+). Using 'General Accounting'`);
            mainTopic = 'General Accounting';
        }

        // ULTRA REFINED DETECTION v2: Enhanced multi-factor threshold with better accuracy
        // Use adaptive threshold based on multiple factors
        const conversationLength = messages.length;
        const userMessageCount = userMessages.length;
        const avgMessageLength = allContent.length / Math.max(messages.length, 1);

        // Enhanced: Consider content depth (longer messages = more detailed discussion)
        const contentDepth = avgMessageLength > 500 ? 'deep' : avgMessageLength > 200 ? 'medium' : 'shallow';

        // Dynamic base threshold based on conversation characteristics
        let baseThreshold = 2;
        if (conversationLength > 15 && contentDepth === 'deep') {
            baseThreshold = 5; // Very long, detailed conversation
        } else if (conversationLength > 10 || contentDepth === 'deep') {
            baseThreshold = 4; // Medium-long or deep conversation
        } else if (conversationLength > 5) {
            baseThreshold = 3; // Medium conversation
        }

        // Calculate subtopic threshold as 10% of main topic (more lenient for better subtopic detection)
        const percentageThreshold = Math.floor(maxCount * 0.10);
        const minSubtopicThreshold = Math.max(baseThreshold, percentageThreshold);

        // SMART ADJUSTMENT: Adaptive thresholds based on main topic strength
        let finalThreshold = minSubtopicThreshold;
        if (maxCount < 3) {
            // Very weak main topic - be strict to avoid noise
            finalThreshold = Math.max(minSubtopicThreshold, 2);
        } else if (maxCount < 8) {
            // Moderate main topic - moderate strictness
            finalThreshold = Math.max(minSubtopicThreshold, Math.floor(maxCount * 0.20));
        } else if (maxCount >= 15) {
            // Strong main topic - can be more lenient with subtopics
            finalThreshold = Math.max(2, Math.floor(maxCount * 0.08));
        }

        // BONUS: User engagement bonus - more user messages = more genuine exploration
        if (userMessageCount > 5 && userMessageCount / Math.max(messages.length, 1) > 0.4) {
            finalThreshold = Math.max(2, finalThreshold - 1);
        }

        // ENHANCED: Also consider if topic count is significantly above noise level
        const noiseLevel = 2; // Minimum matches to be considered meaningful

        const subTopics = Object.entries(topicCounts)
            .filter(([topic, count]) => {
                // Don't include if it's the main topic
                if (topic === mainTopic) return false;
                // Must be above noise level
                if (count <= noiseLevel) return false;
                // Must meet final threshold
                if (count < finalThreshold) return false;
                // Must be at least 12% of main topic strength (balanced threshold)
                if (count < maxCount * 0.12) return false;
                // Ensure the subtopic is meaningfully represented
                return true;
            })
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3) // Max 3 subtopics
            .map(([topic]) => topic);

        console.log(`[LucasQuest] 🎯 ULTRA-REFINED Topic Detection v2 Results:`);
        console.log(`  📚 Main Topic: "${mainTopic}" (${maxCount} keyword matches)`);
        console.log(`  📊 All Topic Scores:`, Object.entries(topicCounts)
            .filter(([t, c]) => c > 0)
            .sort((a, b) => b[1] - a[1])
            .map(([t, c]) => `${t}(${c})`)
            .join(', '));
        console.log(`  🔖 Selected Sub-Topics: ${subTopics.length > 0 ? subTopics.join(', ') : 'None (focused single-topic discussion)'}`);
        console.log(`  ⚙️ Detection Parameters:`);
        console.log(`     - Messages: ${conversationLength} total, ${userMessageCount} from user (${Math.round(userMessageCount / Math.max(conversationLength, 1) * 100)}%)`);
        console.log(`     - Content depth: ${contentDepth} (avg ${Math.round(avgMessageLength)} chars/msg)`);
        console.log(`     - Thresholds: base=${baseThreshold}, final=${finalThreshold}, noise=${noiseLevel}`);
        console.log(`     - Min percentage for subtopic: 12%, Max subtopics: 3`);

        // Estimate difficulty based on content
        const complexityIndicators = {
            high: /\b(complex|advanced|multi.?step|comprehensive|integrated|analysis|professional judgment|exception|unless|however|provided that)\b/gi,
            medium: /\b(calculate|compute|determine|explain|discuss|compare|contrast)\b/gi,
            low: /\b(define|what is|basic|simple|identify|list)\b/gi
        };

        const highCount = (allContent.match(complexityIndicators.high) || []).length;
        const mediumCount = (allContent.match(complexityIndicators.medium) || []).length;
        const lowCount = (allContent.match(complexityIndicators.low) || []).length;

        let difficulty: 'easy' | 'medium' | 'hard' | 'expert' = 'medium';
        let complexity = 5;

        if (highCount > mediumCount && highCount > lowCount) {
            difficulty = messages.length > 20 ? 'expert' : 'hard';
            complexity = messages.length > 20 ? 9 : 7;
        } else if (mediumCount > lowCount) {
            difficulty = messages.length > 15 ? 'hard' : 'medium';
            complexity = messages.length > 15 ? 6 : 5;
        } else {
            difficulty = 'easy';
            complexity = 3;
        }

        // Extract key terms
        const keyTerms = this.extractKeyTerms(allContent);

        // NEW: Detect SPECIFIC subtopic for precise quizzes
        // This narrows down "Taxation" to "Income Tax" or "VAT" based on what's actually discussed
        const specificSubtopics: Record<string, { parent: string; pattern: RegExp; }> = {
            // Taxation specifics
            'Income Tax (Individual)': { parent: 'Taxation', pattern: /\b(individual income tax|compensation income|personal income tax|personal exemption|dependent|graduated tax rate|8%\s*gross|train law individual|individual taxpayer|self.?employed individual|mixed income|purely compensation|itemized deduction|osd|optional standard)\b/gi },
            'Income Tax (Corporate)': { parent: 'Taxation', pattern: /\b(corporate tax|corporate income tax|cit|domestic corporation|resident foreign|non.?resident foreign|rcit|mcit|minimum corporate|create act|domestic corp|foreign corp|branch profit|improperly accumulated|iaet)\b/gi },
            'VAT': { parent: 'Taxation', pattern: /\b(vat|value.?added tax|input tax|output tax|12%|zero.?rated|vat.?exempt|effectively zero|excess input|input vat|output vat|vat.?able|vat threshold|vatable)\b/gi },
            'Percentage Tax': { parent: 'Taxation', pattern: /\b(percentage tax|3%|non.?vat|gross receipt tax)\b/gi },
            'Withholding Tax': { parent: 'Taxation', pattern: /\b(withholding tax|ewt|expanded withholding|final withholding tax|fwt|creditable withholding|cwt|withholding on compensation|withholding agent)\b/gi },
            'Estate Tax': { parent: 'Taxation', pattern: /\b(estate tax|gross estate|net estate|vanishing deduction|standard deduction estate|family home deduction|6%\s*estate)\b/gi },
            'Donor\'s Tax': { parent: 'Taxation', pattern: /\b(donor.?s tax|donation|donee|donor|6%\s*donor)\b/gi },
            'Capital Gains Tax': { parent: 'Taxation', pattern: /\b(capital gains tax|cgt|6%\s*capital|15%\s*stock|sale of shares|real property capital|capital asset sale)\b/gi },

            // Financial Accounting specifics
            'Revenue Recognition (PFRS 15)': { parent: 'Financial Accounting', pattern: /\b(pfrs 15|revenue recognition|five.?step|five steps|performance obligation|transaction price|variable consideration|contract modification|principal.?agent|bill.?and.?hold|consignment sale|significant financing)\b/gi },
            'Lease Accounting (PFRS 16)': { parent: 'Financial Accounting', pattern: /\b(pfrs 16|lease|right.?of.?use|rou asset|lease liability|finance lease|operating lease|lessee|lessor|incremental borrowing rate|lease term)\b/gi },
            'Financial Instruments (PFRS 9)': { parent: 'Financial Accounting', pattern: /\b(pfrs 9|financial instrument|fvpl|fvoci|amortized cost|expected credit loss|ecl|hedge accounting|fair value through)\b/gi },
            'Inventory (PAS 2)': { parent: 'Financial Accounting', pattern: /\b(pas 2|inventory|fifo|lifo|weighted average|perpetual|periodic|net realizable value|nrv|inventory valuation|cost of goods)\b/gi },
            'PPE & Depreciation (PAS 16)': { parent: 'Financial Accounting', pattern: /\b(pas 16|ppe|property plant|depreciation|straight.?line|double declining|sum of years|units of production|useful life|salvage value|residual value|revaluation model|cost model)\b/gi },
            'Intangible Assets (PAS 38)': { parent: 'Financial Accounting', pattern: /\b(pas 38|intangible asset|goodwill|patent|copyright|trademark|franchise|amortization|indefinite.?lived|finite.?lived|research and development|r\s*&\s*d)\b/gi },
            'Impairment (PAS 36)': { parent: 'Financial Accounting', pattern: /\b(pas 36|impairment|impairment loss|recoverable amount|value in use|fair value less cost|impairment test|impairment indicator|cgu|cash.?generating unit)\b/gi },

            // Auditing specifics
            'Audit Risk & Materiality': { parent: 'Auditing & Assurance', pattern: /\b(audit risk|inherent risk|control risk|detection risk|materiality|performance materiality|tolerable misstatement|significant risk|risk of material misstatement|rmm)\b/gi },
            'Audit Evidence & Procedures': { parent: 'Auditing & Assurance', pattern: /\b(audit evidence|substantive procedure|test of control|analytical procedure|sufficient appropriate|inspection|observation|inquiry|confirmation|recalculation|reperformance)\b/gi },
            'Audit Reports & Opinions': { parent: 'Auditing & Assurance', pattern: /\b(audit opinion|unmodified|qualified opinion|adverse opinion|disclaimer|emphasis of matter|other matter|modified opinion|audit report)\b/gi },
            'Internal Control (COSO)': { parent: 'Auditing & Assurance', pattern: /\b(internal control|coso|control environment|control activities|risk assessment|information and communication|monitoring|control deficiency|significant deficiency|material weakness)\b/gi },

            // Management Accounting specifics
            'CVP Analysis': { parent: 'Management Accounting / MAS', pattern: /\b(cvp|cost.?volume.?profit|break.?even|contribution margin|margin of safety|target profit|operating leverage|sales mix|cm ratio)\b/gi },
            'Budgeting & Variance': { parent: 'Management Accounting / MAS', pattern: /\b(budget|budgeting|variance|favorable|unfavorable|price variance|quantity variance|efficiency variance|master budget|flexible budget|static budget)\b/gi },
            'Standard Costing': { parent: 'Management Accounting / MAS', pattern: /\b(standard cost|standard costing|material price variance|material quantity variance|labor rate variance|labor efficiency variance|overhead variance|volume variance)\b/gi },
            'Activity-Based Costing': { parent: 'Management Accounting / MAS', pattern: /\b(activity.?based cost|abc|cost driver|cost pool|activity rate|abm|activity.?based management)\b/gi },

            // Advanced Accounting specifics
            'Business Combinations': { parent: 'Advanced Accounting', pattern: /\b(business combination|acquisition method|goodwill|bargain purchase|acquirer|acquiree|consideration transferred|identifiable asset|net asset acquired)\b/gi },
            'Consolidated Financial Statements': { parent: 'Advanced Accounting', pattern: /\b(consolidat|consolidated statement|elimination|intercompany|parent|subsidiary|nci|non.?controlling interest|minority interest|upstream|downstream)\b/gi },

            // Business Law specifics  
            'Corporation Law': { parent: 'Business Law / RFBT', pattern: /\b(revised corporation code|rcc|corporation|corporate|articles of incorporation|by.?laws|board of directors|stockholder|shareholder|quorum|appraisal right|pre.?emptive right)\b/gi },
            'Negotiable Instruments': { parent: 'Business Law / RFBT', pattern: /\b(negotiable instrument|nil|promissory note|bill of exchange|check|holder in due course|indorsement|endorsement|negotiation|dishonor|protest)\b/gi },
            'Obligations & Contracts': { parent: 'Business Law / RFBT', pattern: /\b(obligation|contract|civil code|consent|object|cause|void|voidable|unenforceable|rescissible|novation|compensation|confusion|remission)\b/gi },
        };

        // Find the most specific topic being discussed
        let specificTopic: string | null = null;
        let specificTopicCount = 0;

        Object.entries(specificSubtopics).forEach(([topic, config]) => {
            const matches = allContent.match(config.pattern) || [];
            if (matches.length >= 3 && matches.length > specificTopicCount) {
                // Only use specific topic if it's within the detected main topic domain
                if (mainTopic === config.parent || mainTopic === 'General Accounting') {
                    specificTopic = topic;
                    specificTopicCount = matches.length;
                }
            }
        });

        // If a specific topic is found, use it as the main topic
        if (specificTopic) {
            console.log(`[LucasQuest] 🎯 SPECIFIC TOPIC DETECTED: "${specificTopic}" (${specificTopicCount} matches) [within ${mainTopic}]`);
            mainTopic = specificTopic;
        }

        return {
            mainTopic,
            subTopics,
            difficulty,
            estimatedComplexity: complexity,
            keyTerms
        };
    }

    /**
     * Extract key accounting/CPA terms from content
     */
    private extractKeyTerms(content: string): string[] {
        const termPatterns = [
            /\b(pfrs|pas|isa|psa)\s*\d+\b/gi,
            /\b(section|nirc|rr|rmo)\s*\d+/gi,
            /\b[A-Z]{3,}\b/g, // Acronyms
        ];

        const terms = new Set<string>();
        termPatterns.forEach(pattern => {
            const matches = content.match(pattern) || [];
            matches.forEach((m: string) => terms.add(m.toUpperCase()));
        });

        return Array.from(terms).slice(0, 10);
    }

    /**
     * Calculate question type distribution based on item count and weights
     */
    private calculateQuestionDistribution(itemCount: number): QuestionTypeDistribution {
        const types = this.config.questionTypes;
        const weights = this.config.questionTypeWeights;

        // Get enabled types and their weights
        const enabledTypes: { type: QuestionType; weight: number }[] = [];
        if (types.multipleChoice) enabledTypes.push({ type: 'multipleChoice', weight: weights.multipleChoice });
        if (types.trueFalse) enabledTypes.push({ type: 'trueFalse', weight: weights.trueFalse });
        if (types.fillInBlank) enabledTypes.push({ type: 'fillInBlank', weight: weights.fillInBlank });
        if (types.shortAnswer) enabledTypes.push({ type: 'shortAnswer', weight: weights.shortAnswer });
        if (types.computation) enabledTypes.push({ type: 'computation', weight: weights.computation });
        if (types.essay) enabledTypes.push({ type: 'essay', weight: weights.essay });

        // Calculate total weight
        const totalWeight = enabledTypes.reduce((sum, t) => sum + t.weight, 0);

        // Distribute items proportionally
        const distribution: QuestionTypeDistribution = {
            multipleChoice: 0,
            trueFalse: 0,
            fillInBlank: 0,
            shortAnswer: 0,
            computation: 0,
            essay: 0
        };

        let remaining = itemCount;
        enabledTypes.forEach((t, idx) => {
            if (idx === enabledTypes.length - 1) {
                // Last type gets remaining items
                distribution[t.type] = remaining;
            } else {
                const count = Math.round((t.weight / totalWeight) * itemCount);
                distribution[t.type] = Math.min(count, remaining);
                remaining -= distribution[t.type];
            }
        });

        return distribution;
    }

    /**
     * Get enabled question types
     */
    private getEnabledQuestionTypes(): QuestionType[] {
        const types = this.config.questionTypes;
        const enabled: QuestionType[] = [];
        if (types.multipleChoice) enabled.push('multipleChoice');
        if (types.trueFalse) enabled.push('trueFalse');
        if (types.fillInBlank) enabled.push('fillInBlank');
        if (types.shortAnswer) enabled.push('shortAnswer');
        if (types.computation) enabled.push('computation');
        if (types.essay) enabled.push('essay');
        return enabled;
    }

    /**
     * Generate quest based on conversation
     */
    generateQuest(sessionId: string, messages: Message[]): QuestData | null {
        if (!this.canTriggerQuest(sessionId)) {
            return null;
        }

        const topics = this.analyzeTopics(messages);
        const conversationDuration = Date.now() - (this.conversationStartTimes.get(sessionId) || Date.now());
        const durationMinutes = conversationDuration / (60 * 1000);

        // Calculate item count based on duration (10-50 items)
        // 5 mins = 10 items, 30 mins = 50 items
        const itemCount = Math.min(
            this.config.maxItems,
            Math.max(
                this.config.minItems,
                Math.floor(10 + (durationMinutes - 5) * 1.6)
            )
        );

        // Calculate question distribution
        const questionDistribution = this.config.enableMixedQuestions
            ? this.calculateQuestionDistribution(itemCount)
            : { multipleChoice: itemCount, trueFalse: 0, fillInBlank: 0, shortAnswer: 0, computation: 0, essay: 0 };

        // Calculate time limit based on question types
        // Essays need more time than multiple choice
        let timeLimit = 0;
        timeLimit += questionDistribution.multipleChoice * 1;      // 1 min per MC
        timeLimit += questionDistribution.trueFalse * 0.5;         // 30 sec per T/F
        timeLimit += questionDistribution.fillInBlank * 1;         // 1 min per fill-in
        timeLimit += questionDistribution.shortAnswer * 2;         // 2 min per short answer
        timeLimit += questionDistribution.computation * 3;         // 3 min per computation
        timeLimit += questionDistribution.essay * 5;               // 5 min per essay
        timeLimit = Math.ceil(Math.max(timeLimit, itemCount * 1.5));

        const quest: QuestData = {
            id: `quest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            sessionId,
            topics,
            itemCount,
            difficulty: topics.difficulty,
            timeLimit,
            createdAt: Date.now(),
            expiresAt: Date.now() + (5 * 60 * 1000), // 5 minute expiry
            status: 'pending',
            questionDistribution,
            enabledQuestionTypes: this.getEnabledQuestionTypes()
        };

        this.activeQuests.set(sessionId, quest);
        this.notify(quest);

        console.log(`[LucasQuest] Generated quest for session ${sessionId}:`, {
            id: quest.id,
            topics: quest.topics,
            itemCount: quest.itemCount,
            difficulty: quest.difficulty,
            questionDistribution: quest.questionDistribution
        });

        return quest;
    }

    /**
     * Get active quest for session
     */
    getActiveQuest(sessionId: string): QuestData | null {
        const quest = this.activeQuests.get(sessionId);

        // Check if quest has expired
        if (quest && quest.status === 'pending' && Date.now() > quest.expiresAt) {
            quest.status = 'expired';
            this.activeQuests.delete(sessionId);
            this.notify(null);
            return null;
        }

        return quest || null;
    }

    /**
     * Accept a quest
     */
    acceptQuest(sessionId: string): QuestData | null {
        const quest = this.activeQuests.get(sessionId);
        if (!quest || quest.status !== 'pending') {
            return null;
        }

        quest.status = 'active';
        this.activeQuests.set(sessionId, quest);

        console.log(`[LucasQuest] Quest accepted: ${quest.id}`);

        return quest;
    }

    /**
     * Cancel a quest
     */
    cancelQuest(sessionId: string): void {
        const quest = this.activeQuests.get(sessionId);
        if (quest) {
            quest.status = 'cancelled';
            this.activeQuests.delete(sessionId);
            this.lastQuestTime.set(sessionId, Date.now());
            this.questStreak = 0; // Reset streak on cancel
            this.saveState();
            this.notify(null);

            console.log(`[LucasQuest] Quest cancelled: ${quest.id}`);
        }
    }

    /**
     * Complete a quest and calculate rewards
     */
    completeQuest(sessionId: string, score: number, totalItems: number): QuestResult | null {
        const quest = this.activeQuests.get(sessionId);
        if (!quest || quest.status !== 'active') {
            return null;
        }

        quest.status = 'completed';
        quest.score = score;
        quest.totalItems = totalItems;

        // Calculate base reward
        const scorePercent = (score / totalItems) * 100;
        const difficultyMultiplier = DIFFICULTY_MULTIPLIERS[quest.difficulty];

        let baseReward = score * this.config.baseTokenReward * difficultyMultiplier;
        let bonusTokens = 0;
        let perfectBonus = false;
        let streakBonus = 0;

        // Perfect score bonus
        if (scorePercent === 100) {
            bonusTokens += baseReward * PERFECT_SCORE_BONUS;
            perfectBonus = true;
        } else if (scorePercent >= 90) {
            bonusTokens += baseReward * HIGH_SCORE_BONUS;
        }

        // Streak bonus
        if (this.questStreak > 0) {
            streakBonus = baseReward * (STREAK_BONUS_PER_QUEST * Math.min(this.questStreak, 5));
            bonusTokens += streakBonus;
        }

        // Item count bonus (more items = more reward)
        if (totalItems >= 30) {
            bonusTokens += baseReward * 0.2;
        } else if (totalItems >= 20) {
            bonusTokens += baseReward * 0.1;
        }

        const totalReward = Math.round(baseReward + bonusTokens);
        quest.tokensRewarded = totalReward;

        // Update state
        this.completedQuests.push(quest);
        this.activeQuests.delete(sessionId);
        this.lastQuestTime.set(sessionId, Date.now());

        // Update streak
        if (scorePercent >= 60) {
            this.questStreak++;
        } else {
            this.questStreak = 0;
        }

        // Reset conversation timer for next quest
        this.conversationStartTimes.set(sessionId, Date.now());

        this.saveState();
        this.notify(null);

        const result: QuestResult = {
            questId: quest.id,
            score,
            totalItems,
            difficulty: quest.difficulty,
            tokensRewarded: totalReward,
            bonusTokens: Math.round(bonusTokens),
            perfectBonus,
            streakBonus: Math.round(streakBonus)
        };

        console.log(`[LucasQuest] Quest completed:`, result);

        return result;
    }

    /**
     * Generate quiz prompt for AI with mixed question types
     */
    generateQuestPrompt(quest: QuestData): string {
        const difficultyDesc = {
            easy: 'basic understanding level, focusing on definitions and simple concepts',
            medium: 'intermediate level with some application questions',
            hard: 'challenging questions requiring analysis and multi-step reasoning',
            expert: 'CPALE-level difficulty with complex scenarios and trick questions'
        };

        const dist = quest.questionDistribution || {
            multipleChoice: quest.itemCount, trueFalse: 0, fillInBlank: 0,
            shortAnswer: 0, computation: 0, essay: 0
        };

        // Build question type requirements
        const questionTypeInstructions: string[] = [];

        if (dist.multipleChoice > 0) {
            questionTypeInstructions.push(`
## MULTIPLE CHOICE (${dist.multipleChoice} questions)
- Type: "multipleChoice"
- 4 options (A, B, C, D)
- Clear, unambiguous correct answer
- Plausible distractors`);
        }

        if (dist.trueFalse > 0) {
            questionTypeInstructions.push(`
## TRUE/FALSE (${dist.trueFalse} questions)
- Type: "trueFalse"
- Statement that is definitively true or false
- Avoid "sometimes" or ambiguous statements`);
        }

        if (dist.fillInBlank > 0) {
            questionTypeInstructions.push(`
## FILL IN THE BLANK (${dist.fillInBlank} questions)
- Type: "fillInBlank"
- Use _____ for blanks
- acceptableAnswers: array of valid answers (case-insensitive)`);
        }

        if (dist.shortAnswer > 0) {
            questionTypeInstructions.push(`
## SHORT ANSWER (${dist.shortAnswer} questions)
- Type: "shortAnswer"
- Requires 1-3 sentence response
- keyPoints: array of expected points in answer`);
        }

        if (dist.computation > 0) {
            questionTypeInstructions.push(`
## COMPUTATION (${dist.computation} questions)
- Type: "computation"
- Provide clear numerical problem
- Show step-by-step solution in explanation
- correctAnswer: exact numerical answer`);
        }

        if (dist.essay > 0) {
            questionTypeInstructions.push(`
## ESSAY (${dist.essay} questions)
- Type: "essay"
- Open-ended question requiring detailed analysis and professional judgment
- rubric: array of scoring criteria (Understanding, Application, Accuracy, Completeness)
- maxPoints: 100 (will be graded on 1.0-5.0 Philippine grade scale)
- keyPoints: array of key concepts that should be covered in the answer
- sampleAnswer: ideal comprehensive answer for reference

ESSAY GRADING SCALE:
- 1.0 = 96-100% (Excellent - comprehensive, accurate, well-organized)
- 1.25 = 93-95%
- 1.5 = 90-92% (Very Good)
- 1.75 = 87-89%
- 2.0 = 84-86% (Good)
- 2.25 = 81-83%
- 2.5 = 78-80% (Satisfactory)
- 2.75 = 75-77%
- 3.0 = 72-74% (Passing)
- 5.0 = Below 72% (Failed)`);
        }

        return `
You are LUCA's Quest Generator. Generate a quiz for a CPA student based on their conversation.

**CRITICAL CONTEXT RULES - MUST FOLLOW:**
1. ⚠️ Generate questions EXCLUSIVELY about topics discussed in THIS conversation
2. ✅ Main topic: ${quest.topics.mainTopic}${quest.topics.subTopics.length > 0 ? `\n3. ✅ Also covered: ${quest.topics.subTopics.join(', ')}` : '\n3. ❌ NO OTHER TOPICS - Only ' + quest.topics.mainTopic}
4. ❌ DO NOT create questions from other CPA subjects not mentioned in the conversation
5. ❌ DO NOT mix in topics that weren't discussed (e.g., if only Taxation was discussed, NO Auditing, NO RFBT, NO FAR questions)
6. ✅ Base ALL questions on these actual topics discussed: ${quest.topics.keyTerms.slice(0, 10).join(', ')}
7. ⚠️ If conversation is about "${quest.topics.mainTopic}" ONLY, then ALL ${quest.itemCount} questions must be about "${quest.topics.mainTopic}"

**DIFFICULTY:** ${quest.difficulty} (${difficultyDesc[quest.difficulty]})

QUESTION TYPE DISTRIBUTION:
${questionTypeInstructions.join('\n')}

IMPORTANT REQUIREMENTS:
1. Generate questions in RANDOM order (mix the types throughout)
2. Questions should test understanding from our conversation
3. Match difficulty level: ${quest.difficulty}
4. Be specific to accounting/CPA concepts discussed

FORMAT YOUR RESPONSE AS JSON:
{
  "questions": [
    {
      "id": 1,
      "type": "multipleChoice",
      "question": "What is the correct treatment for...?",
      "options": { "A": "...", "B": "...", "C": "...", "D": "..." },
      "correctAnswer": "B",
      "explanation": "..."
    },
    {
      "id": 2,
      "type": "trueFalse",
      "question": "Revenue is recognized when...",
      "correctAnswer": true,
      "explanation": "..."
    },
    {
      "id": 3,
      "type": "fillInBlank",
      "question": "The formula for depreciation using straight-line method is Cost minus _____ divided by _____.",
      "acceptableAnswers": [["salvage value", "residual value"], ["useful life", "estimated life"]],
      "explanation": "..."
    },
    {
      "id": 4,
      "type": "shortAnswer",
      "question": "Explain the difference between...",
      "keyPoints": ["Point 1", "Point 2", "Point 3"],
      "explanation": "..."
    },
    {
      "id": 5,
      "type": "computation",
      "question": "Calculate the depreciation expense...",
      "correctAnswer": 50000,
      "explanation": "Step 1: ... Step 2: ... Answer: P50,000"
    },
    {
      "id": 6,
      "type": "essay",
      "question": "Discuss the implications of...",
      "rubric": ["Understanding of Concept (25%)", "Application & Analysis (30%)", "Technical Accuracy (25%)", "Completeness & Organization (20%)"],
      "maxPoints": 100,
      "keyPoints": ["key concept 1", "key concept 2", "relevant standard", "practical application"],
      "sampleAnswer": "A comprehensive answer would include..."
    }
  ]
}

Shuffle the question types randomly throughout the quiz!`;
    }

    /**
     * Trigger a quest with optional runtime config
     */
    triggerQuest(sessionId: string, messages: Message[], runtimeConfig?: any): QuestData | null {
        // Apply runtime config if provided (from admin panel)
        if (runtimeConfig) {
            // Map admin config to service config
            if (runtimeConfig.minConversationTime !== undefined) {
                this.config.minConversationTime = runtimeConfig.minConversationTime * 60 * 1000; // Convert minutes to ms
            }
            if (runtimeConfig.questCooldown !== undefined) {
                this.config.questCooldown = runtimeConfig.questCooldown * 60 * 1000; // Convert minutes to ms
            }
            if (runtimeConfig.minItems !== undefined) {
                this.config.minItems = runtimeConfig.minItems;
            }
            if (runtimeConfig.maxItems !== undefined) {
                this.config.maxItems = runtimeConfig.maxItems;
            }
            if (runtimeConfig.baseRewards?.easy !== undefined) {
                this.config.baseTokenReward = runtimeConfig.baseRewards.easy;
            }
            if (runtimeConfig.questionTypes) {
                // Update question types from admin config
                Object.entries(runtimeConfig.questionTypes).forEach(([type, settings]: [string, any]) => {
                    if (this.config.questionTypes[type as keyof typeof this.config.questionTypes] !== undefined) {
                        this.config.questionTypes[type as keyof typeof this.config.questionTypes] = settings.enabled;
                    }
                    if (this.config.questionTypeWeights[type as keyof typeof this.config.questionTypeWeights] !== undefined) {
                        this.config.questionTypeWeights[type as keyof typeof this.config.questionTypeWeights] = settings.weight;
                    }
                });
            }
        }

        return this.generateQuest(sessionId, messages);
    }

    /**
     * Update configuration (for admin dashboard)
     */
    updateConfig(newConfig: Partial<QuestConfig>): void {
        this.config = { ...this.config, ...newConfig };
        this.saveConfig();
        console.log('[LucasQuest] Config updated:', this.config);
    }

    /**
     * Save config to localStorage
     */
    private saveConfig(): void {
        try {
            localStorage.setItem('lucas_quest_config', JSON.stringify(this.config));
        } catch (error) {
            console.error('[LucasQuest] Failed to save config:', error);
        }
    }

    /**
     * Load config from localStorage (called in constructor)
     */
    private loadConfig(): QuestConfig {
        try {
            const saved = localStorage.getItem('lucas_quest_config');
            if (saved) {
                return { ...DEFAULT_CONFIG, ...JSON.parse(saved) };
            }
        } catch (error) {
            console.error('[LucasQuest] Failed to load config:', error);
        }
        return DEFAULT_CONFIG;
    }

    /**
     * Get current configuration
     */
    getConfig(): QuestConfig {
        return { ...this.config };
    }

    /**
     * Get quest statistics
     */
    getStats(): {
        totalCompleted: number;
        totalTokensEarned: number;
        averageScore: number;
        currentStreak: number;
        bestStreak: number;
    } {
        const stats = {
            totalCompleted: this.completedQuests.length,
            totalTokensEarned: this.completedQuests.reduce((sum, q) => sum + (q.tokensRewarded || 0), 0),
            averageScore: 0,
            currentStreak: this.questStreak,
            bestStreak: this.questStreak
        };

        if (this.completedQuests.length > 0) {
            const totalScore = this.completedQuests.reduce((sum, q) => {
                if (q.score !== undefined && q.totalItems) {
                    return sum + (q.score / q.totalItems) * 100;
                }
                return sum;
            }, 0);
            stats.averageScore = Math.round(totalScore / this.completedQuests.length);
        }

        return stats;
    }
}

// Singleton instance
export const lucasQuestService = new LucasQuestService();

// Export convenience functions
export const startQuestTracking = (sessionId: string) =>
    lucasQuestService.startTracking(sessionId);
export const canTriggerQuest = (sessionId: string) =>
    lucasQuestService.canTriggerQuest(sessionId);
export const generateQuest = (sessionId: string, messages: Message[]) =>
    lucasQuestService.generateQuest(sessionId, messages);
export const getActiveQuest = (sessionId: string) =>
    lucasQuestService.getActiveQuest(sessionId);
export const acceptQuest = (sessionId: string) =>
    lucasQuestService.acceptQuest(sessionId);
export const cancelQuest = (sessionId: string) =>
    lucasQuestService.cancelQuest(sessionId);
export const completeQuest = (sessionId: string, score: number, totalItems: number) =>
    lucasQuestService.completeQuest(sessionId, score, totalItems);
export const getQuestPrompt = (quest: QuestData) =>
    lucasQuestService.generateQuestPrompt(quest);
export const getQuestStats = () =>
    lucasQuestService.getStats();
export const subscribeToQuests = (callback: (quest: QuestData | null) => void) =>
    lucasQuestService.subscribe(callback);
export const getTimeUntilNextQuest = (sessionId: string) =>
    lucasQuestService.getTimeUntilNextQuest(sessionId);
export const getQuestConfig = () =>
    lucasQuestService.getConfig();
export const updateQuestConfig = (config: Partial<QuestConfig>) =>
    lucasQuestService.updateConfig(config);

